/**
 * CRS 신분증 종류에 딸린 파생 규칙 — **단일 출처**.
 *
 * CRS(roc-registration) 의 `onIdentificationTypeChanged()` 를 실측해 옮긴 것이다.
 * 신분증 종류를 고르면 CRS 는 시민권·국적·인종·2차 신분증을 **자동으로 정하고 잠근다**.
 * 우리도 같은 시점에 같은 값을 화면에 반영해야 사용자가 본 값과 전송값이 일치한다.
 *
 *   MYKAD / MYTENTERA → 시민권 MALAYSIAN,      국적 MALAYSIA 고정
 *   MYPR              → 시민권 PERM RESIDENT,  인종 FOREIGNER 고정
 *   MYKAS             → 시민권 PERM RESIDENT,  **1차 신분증 만료일** 필수
 *   NID(여권)         → 시민권 NON-MALAYSIAN,  인종 FOREIGNER 고정, 2차 신분증 PASSPORT
 *
 * 2차 신분증은 CRS 에서 **1차와 코드 공간이 다르다**(31006 PASSPORT / 31007 OLD IC /
 * 41165 POLICE ID / 34100 NONE). 코드 변환은 useCrsDraftPayload 가 담당한다.
 *
 * ★ 2026-09-21 CRS 실화면(roc-name-search-direct-incorp-guaranteed-incorp/main-form)
 *   Director Details / Member Details 다이얼로그에서 1차 신분증을 다섯 값으로 모두
 *   바꿔 가며 필드 출현을 직접 확인했다. 결과:
 *
 *     ID Type                    | 추가로 나타나는 필드
 *     MYKAD / MYPR / MYTENTERA   | (없음)
 *     MYKAS                      | Identification Expiry Date *
 *     FOREIGNER NATIONAL ID(NID) | Secondary Identification Type * / No. * / Expiry Date *
 *
 *   즉 2차 신분증은 **NID 에서만** 나온다. 이전에는 MYPR·MYKAS 에서도 열어 두었는데
 *   CRS 화면에는 그 입력이 아예 없다. MYKAS 가 요구하는 것은 2차 신분증이 아니라
 *   **1차 신분증의 만료일**이다.
 */

/** Lettaga 신분증 값 → CRS 심볼릭 키. CRS 1차 목록에 없으면 null. */
export function toCrsIdKey(idType: unknown): string | null {
  const k = String(idType ?? '').toUpperCase().replace(/[_-]/g, ' ').trim();
  switch (k) {
    case 'NRIC': case 'MYKAD': return 'MYKAD';
    case 'MYKAS': return 'MYKAS';
    case 'PR NUMBER': case 'MYPR': return 'MYPR';
    case 'ARMY ID': case 'MYTENTERA': return 'MYTENTERA';
    case 'PASSPORT': case 'NID': case 'FOREIGNER NATIONAL ID': return 'NID';
    default: return null;
  }
}

export interface CrsIdentityDerived {
  /** CRS 심볼릭 키. null 이면 CRS 1차 목록에 없는 값이라 제출할 수 없다. */
  crsKey: string | null;
  /** 국적이 고정되는 경우 그 값. 화면에서 읽기전용으로 두어야 한다. */
  forcedNationality: string | null;
  /** 인종이 고정되는 경우 그 값. 화면에서 읽기전용으로 두어야 한다. */
  forcedRace: string | null;
  /** CRS citizenshipTypeCode 키. Lettaga 는 입력받지 않고 여기서 도출한다. */
  citizenshipKey: 'M' | 'PR' | 'NM' | null;
  /** 2차 신분증(여권 등)을 받아야 하는가. CRS 는 NID 에서만 받는다. */
  needsSecondaryId: boolean;
  /** 2차 신분증 종류가 PASSPORT 로 고정되는가(CRS 는 이사·멤버 모두 disabled 로 고정). */
  secondaryIdFixedToPassport: boolean;
  /** **1차** 신분증 만료일을 받아야 하는가. CRS 는 MYKAS 에서만 받는다(필수). */
  needsIdExpiryDate: boolean;
  /**
   * "I don't have a passport" 체크박스를 보여 줄 수 있는가.
   * CRS 는 **멤버(주주) 화면의 NID 에만** 둔다 — 이사 화면에는 없고, 이사는 여권이 필수다.
   * 이사/주주 구분은 화면 쪽에서 하고, 여기서는 신분증 조건만 판정한다.
   */
  allowsNoPassport: boolean;
}

export function crsIdentityRules(idType: unknown): CrsIdentityDerived {
  const crsKey = toCrsIdKey(idType);
  const isLocal = crsKey === 'MYKAD' || crsKey === 'MYTENTERA';
  const isForeigner = crsKey === 'NID';
  const isPr = crsKey === 'MYPR';
  return {
    crsKey,
    forcedNationality: isLocal ? 'MALAYSIA' : null,
    forcedRace: isPr || isForeigner ? 'FOREIGNER' : null,
    citizenshipKey: isLocal ? 'M' : isPr ? 'PR' : crsKey ? 'NM' : null,
    // CRS 실화면 확인: 2차 신분증 입력은 NID 에서만 나온다(MYPR·MYKAS 에는 없음).
    needsSecondaryId: isForeigner,
    secondaryIdFixedToPassport: isForeigner,
    // CRS 실화면 확인: MYKAS 만 1차 신분증 만료일(Identification Expiry Date *) 을 받는다.
    needsIdExpiryDate: crsKey === 'MYKAS',
    allowsNoPassport: isForeigner,
  };
}

/** 2차 신분증이 **필수**인가(CRS 가 * 를 붙이는 경우). */
export function requiresSecondaryId(idType: unknown): boolean {
  return toCrsIdKey(idType) === 'NID';
}

/** **1차** 신분증 만료일이 필수인가. CRS 는 MYKAS 에만 * 를 붙인다. */
export function requiresIdExpiryDate(idType: unknown): boolean {
  return toCrsIdKey(idType) === 'MYKAS';
}
